import { CobaltTooltip } from '@cae/cobalt-react/components/tooltip';
import { DataValue } from '@shared/components/data-value/DataValue';
import * as React from 'react';

import type {
  TruncatedDisplayResult,
  TruncatedListProps,
} from './TruncatedList.types';

import styles from './TruncatedList.module.css';
import {
  buildTruncatedDisplay,
  DATAVALUE_FALLBACK,
  SEPARATOR,
} from './TruncatedList.utils';

const TruncatedList = React.memo(
  ({
    items,
    limit = items.length,
    tooltipPosition = 'bottom',
  }: Readonly<TruncatedListProps>): React.ReactElement => {
    const itemsRef = React.useRef(items);
    itemsRef.current = items;

    const limitRef = React.useRef(limit);
    limitRef.current = limit;

    const elRef = React.useRef<HTMLSpanElement | null>(null);
    const observerRef = React.useRef<ResizeObserver | null>(null);
    const lastMeasureRef = React.useRef<{
      width: number;
      limit: number;
      items: readonly string[] | null;
    }>({ width: 0, limit: 0, items: null });

    const [displayResult, setDisplayResult] =
      React.useState<TruncatedDisplayResult>(() => {
        const count = Math.min(items.length, limit);
        return {
          visibleCount: count,
          displayText: items.slice(0, count).join(SEPARATOR) || null,
        };
      });

    const measureEl = React.useCallback(
      (el: HTMLSpanElement, width: number): void => {
        if (!width) return;
        const nextItems = itemsRef.current;
        const nextLimit = limitRef.current;
        const last = lastMeasureRef.current;
        if (
          last.width === width &&
          last.limit === nextLimit &&
          last.items === nextItems
        ) {
          return;
        }
        lastMeasureRef.current = {
          width,
          limit: nextLimit,
          items: nextItems,
        };
        const style = window.getComputedStyle(el);
        const font = `${style.fontWeight} ${style.fontSize} ${style.fontFamily}`;
        setDisplayResult(
          buildTruncatedDisplay(nextItems, width, font, nextLimit)
        );
      },
      []
    );

    const containerRef = React.useCallback(
      (el: HTMLSpanElement | null) => {
        elRef.current = el;
        if (!el) {
          observerRef.current?.disconnect();
          observerRef.current = null;
          return;
        }

        const observer = new ResizeObserver(entries => {
          measureEl(el, entries[0]?.contentRect.width ?? 0);
        });

        observerRef.current = observer;
        observer.observe(el);
      },
      [measureEl]
    );

    React.useEffect(() => {
      const el = elRef.current;
      if (!el) return;
      measureEl(el, el.getBoundingClientRect().width);
    }, [items, limit, measureEl]);

    const { displayText } = displayResult;
    const fullText = items.join(SEPARATOR);
    const isTruncated = displayText !== null && displayText !== fullText;

    const textContent = (
      <span className={styles.cellText}>
        <DataValue value={displayText} fallback={DATAVALUE_FALLBACK} />
      </span>
    );

    return (
      <span
        ref={containerRef}
        className={styles.cell}
        data-testid="truncated-list"
      >
        {isTruncated ? (
          <CobaltTooltip
            label={fullText}
            position={tooltipPosition}
            className={styles.tooltip}
          >
            {textContent}
          </CobaltTooltip>
        ) : (
          textContent
        )}
      </span>
    );
  }
);

TruncatedList.displayName = 'TruncatedList';

export { TruncatedList };
